/**
 * LocalStorage persistence for expanded/collapsed state only.
 * Ratings are never persisted; only which items are open/closed.
 */
import type { ChecklistState } from '../types';
import { STORAGE_KEYS, CACHE_CONFIG } from '../constants';

/** Cached state: only expanded per item (no ratings). */
interface CachedExpandedState {
  [itemId: string]: {
    expanded: boolean;
  };
}

/**
 * Checks if localStorage is available
 */
const isLocalStorageAvailable = (): boolean => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

/**
 * Checks if cached data is from a compatible version
 */
const isCacheVersionCompatible = (): boolean => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    const cachedVersion = localStorage.getItem(STORAGE_KEYS.cacheVersion);
    // If no version is stored, it's an old cache - we'll migrate it
    if (!cachedVersion) {
      return false;
    }
    // For now, accept any v2 cache. In the future, we can add version comparison logic
    return cachedVersion.startsWith('2.');
  } catch {
    return false;
  }
};

/**
 * Safely parses JSON with error handling
 * @returns parsed object or null if parsing fails
 */
const safeJsonParse = <T>(json: string): T | null => {
  try {
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
};

/**
 * Validates that a key is a safe item ID format
 */
const isValidItemId = (key: string): boolean => {
  return typeof key === 'string' && /^item-\d+$/.test(key);
};

/**
 * Migrates old cache format to new format (only expanded state)
 */
const migrateOldCache = (): CachedExpandedState => {
  if (!isLocalStorageAvailable()) {
    return {};
  }

  try {
    const oldKey = 'ai-readiness-checklist-v1';
    const saved = localStorage.getItem(oldKey);
    if (saved) {
      const parsed = safeJsonParse<Record<string, unknown>>(saved);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const migrated: CachedExpandedState = {};
        for (const [key, value] of Object.entries(parsed)) {
          // Validate key format and value structure
          if (
            isValidItemId(key) &&
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            'expanded' in value
          ) {
            // Only migrate expanded state, ignore ratings
            migrated[key] = {
              expanded: (value as { expanded: unknown }).expanded === true,
            };
          }
        }
        // Clean up old cache
        localStorage.removeItem(oldKey);
        return migrated;
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to migrate old cache:', error);
    }
  }
  return {};
};

/**
 * Loads expanded state from localStorage (ratings are never cached)
 * @returns CachedExpandedState object or empty object if not found or error occurs
 */
export const loadExpandedState = (): CachedExpandedState => {
  if (!isLocalStorageAvailable()) {
    return {};
  }

  try {
    // Check if we need to migrate old cache
    if (!isCacheVersionCompatible()) {
      const migrated = migrateOldCache();
      if (Object.keys(migrated).length > 0) {
        // Save migrated cache
        saveExpandedState(migrated);
        return migrated;
      }
    }

    const saved = localStorage.getItem(STORAGE_KEYS.checklistState);
    if (saved) {
      // Check cache size
      if (saved.length > CACHE_CONFIG.maxCacheSize) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Cache size exceeds limit, clearing cache');
        }
        clearExpandedState();
        return {};
      }

      const parsed = safeJsonParse<Record<string, unknown>>(saved);
      // Validate that parsed data is an object (not null, not array)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const validated: CachedExpandedState = {};
        for (const [key, value] of Object.entries(parsed)) {
          // Validate key format and value structure
          if (
            isValidItemId(key) &&
            value &&
            typeof value === 'object' &&
            !Array.isArray(value) &&
            'expanded' in value
          ) {
            // Only keep expanded state, ensure it's a boolean
            const expandedValue = (value as { expanded: unknown }).expanded;
            validated[key] = {
              expanded: typeof expandedValue === 'boolean'
                ? expandedValue
                : true, // Default to expanded if invalid
            };
          }
        }
        return validated;
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to load expanded state:', error);
    }
    // If parsing fails, clear corrupted cache
    clearExpandedState();
  }
  return {};
};

/**
 * Saves expanded state to localStorage with debouncing
 * Only saves expanded state, never ratings
 */
let saveTimeout: NodeJS.Timeout | null = null;
let pendingState: CachedExpandedState | null = null;

export const saveExpandedState = (state: CachedExpandedState, immediate = false): boolean => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  // Validate input state
  if (!state || typeof state !== 'object' || Array.isArray(state)) {
    return false;
  }

  // Store pending state for debouncing
  pendingState = state;

  // Clear existing timeout
  if (saveTimeout) {
    clearTimeout(saveTimeout);
    saveTimeout = null;
  }

  const performSave = () => {
    if (!pendingState) return false;

    try {
      const stateToSave = pendingState;
      const serialized = JSON.stringify(stateToSave);
      
      // Check size before saving
      if (serialized.length > CACHE_CONFIG.maxCacheSize) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('State size exceeds cache limit, skipping save');
        }
        return false;
      }

      localStorage.setItem(STORAGE_KEYS.checklistState, serialized);
      localStorage.setItem(STORAGE_KEYS.cacheVersion, CACHE_CONFIG.version);
      pendingState = null;
      return true;
    } catch (error) {
      // Handle quota exceeded errors
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        if (process.env.NODE_ENV === 'development') {
          console.warn('localStorage quota exceeded, clearing old cache');
        }
        clearExpandedState();
        // Try saving a minimal state
        try {
          const minimalState: CachedExpandedState = {};
          localStorage.setItem(STORAGE_KEYS.checklistState, JSON.stringify(minimalState));
          localStorage.setItem(STORAGE_KEYS.cacheVersion, CACHE_CONFIG.version);
        } catch {
          // If still fails, give up
        }
      } else if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to save expanded state:', error);
      }
      pendingState = null;
      return false;
    }
  };

  if (immediate) {
    return performSave();
  }

  // Debounce the save
  saveTimeout = setTimeout(performSave, CACHE_CONFIG.debounceMs);
  return true;
};

/**
 * Clears expanded state from localStorage
 * @returns boolean indicating success
 */
export const clearExpandedState = (): boolean => {
  if (!isLocalStorageAvailable()) {
    return false;
  }

  try {
    localStorage.removeItem(STORAGE_KEYS.checklistState);
    localStorage.removeItem(STORAGE_KEYS.cacheVersion);
    // Also clear old cache if it exists
    localStorage.removeItem('ai-readiness-checklist-v1');
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to clear expanded state:', error);
    }
    return false;
  }
};

/**
 * Legacy function for backwards compatibility - now only handles expanded state
 * @deprecated Use loadExpandedState instead
 */
export const loadChecklistState = (): ChecklistState => {
  const expandedState = loadExpandedState();
  // Convert to ChecklistState format but with no ratings
  const state: ChecklistState = {};
  for (const [key, value] of Object.entries(expandedState)) {
    state[key] = {
      rating: null, // Never restore ratings
      expanded: value.expanded,
      timestamp: null,
    };
  }
  return state;
};

/**
 * Extracts only expanded state from full checklist state (for persisting without ratings).
 */
export const getExpandedStateFromChecklist = (state: ChecklistState): CachedExpandedState => {
  if (!state || typeof state !== 'object' || Array.isArray(state)) {
    return {};
  }

  const expandedState: CachedExpandedState = {};
  for (const [key, value] of Object.entries(state)) {
    if (
      isValidItemId(key) &&
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      'expanded' in value
    ) {
      expandedState[key] = { expanded: value.expanded === true };
    }
  }
  return expandedState;
};

/**
 * Legacy function for backwards compatibility - extracts and saves only expanded state
 * @deprecated Use getExpandedStateFromChecklist + saveExpandedState instead
 */
export const saveChecklistState = (state: ChecklistState): boolean => {
  return saveExpandedState(getExpandedStateFromChecklist(state));
};

/**
 * Legacy function for backwards compatibility
 * @deprecated Use clearExpandedState instead
 */
export const clearChecklistState = clearExpandedState;
